<script setup lang="ts">import { ref as _ref } from 'vue';

import __MACROS_useVModel from "/vue-macros/define-models/use-vmodel";
defineProps({
  value: null
});
import { watch } from 'vue';
import { AlertingHookService, AlertingHookDto, AlertingHookParams } from '@/api';
import SIcon from '@/components/SIcon.vue';
import SEllipsisTooltip from '@/components/SEllipsisTooltip/SEllipsisTooltip.vue';
import ProviderIcons from '@/utils/provider-icons';

type WebhookWithIcon = AlertingHookDto & { icon: string };

interface Emit {
  (event: 'input', value: AlertingHookDto[]): void
}
const emit = defineEmits(["input"]);

const { value: selectedWebhooks } = __MACROS_useVModel(["value", undefined, "input"]);

let loading = _ref(false);
let alertingHooks = _ref<WebhookWithIcon[]>([]);
const searchString = _ref('');

const getWebhookIcon = (webhook: AlertingHookDto): string => {
  const webhookType = webhook?.params?.type;
  switch (webhookType) {
    case AlertingHookParams.type.GOOGLE_CHAT:
      return ProviderIcons.GOOGLE_CHAT;
    case AlertingHookParams.type.DEFAULT:
    default:
      return ProviderIcons.WEBHOOKS_DARK;
  }
};

const getWebhookType = (webhook: AlertingHookDto): string => {
  const webhookType = webhook?.params?.type;
  switch (webhookType) {
    case AlertingHookParams.type.GOOGLE_CHAT:
      return 'Google Chat';
    case AlertingHookParams.type.DEFAULT:
    default:
      return 'Default';
  }
};

const clear = () => {
  selectedWebhooks.value = [];
  emit('input', selectedWebhooks.value);
};

const fetchWebhookTags = async () => {
  loading.value = true;
  const { data } = await AlertingHookService.getAllAlertingHooks({ type: AlertingHookDto.type.WEBHOOK, itemsPerPage: -1, scope: AlertingHookParams.scope.LINKED_EVENTS });
  // Add icons to webhooks based on their params.type
  alertingHooks.value = (data || []).map((webhook: AlertingHookDto): WebhookWithIcon => ({
    ...webhook,
    icon: getWebhookIcon(webhook),
  }));
  loading.value = false;
};

watch(() => searchString.value, () => {
  fetchWebhookTags();
});
</script>

<template lang="pug">
#webhook-tag-selector( style="position: relative;" )
  v-autocomplete.webhook-selector(
    v-model="selectedWebhooks"
    v-on='$listeners'
    :loading="loading"
    :items="alertingHooks"
    :search-input.sync="searchString"
    :hint="$t('monitor_wizard.notification_method.webhooks_hint')"
    @click:clear="clear"
    @change="searchString=''"
    :placeholder="$t('common.words.type_to_search')"
    item-value="id"
    item-text="name"
    data-track="webhook-tag-selector"
    attach="#webhook-tag-selector"
    cache-items dense outlined
    hide-no-data persistent-hint
    return-object multiple clearable )

    template(v-slot:item="data")
      v-list-item(
        :key="data.item.id"
        v-bind="data.attrs"
        v-on="data.on")
        v-list-item-action(style="pointer-events: none;")
          v-simple-checkbox(
            :value="selectedWebhooks.some(w => w.id === data.item.id)"
            color="primary")
        v-list-item-content
          v-list-item-title.d-flex.align-center( style="width: 100%; gap: 16px;" )
            .d-flex.align-center( style="flex: 1; min-width: 0; overflow: hidden;" )
              SIcon(
                :icon="data.item.icon"
                size="default"
                class="mr-2 flex-shrink-0")
              SEllipsisTooltip.text-body-2( :content="data.item.name" )
            span.text-body-2.text--secondary.flex-shrink-0.text-no-wrap {{ getWebhookType(data.item) }}

    template(v-slot:selection="{ item }")
      .d-flex.align-center.mr-4( style="max-width: 300px;" )
        SIcon(
          :icon="item.icon"
          size="default"
          class="mr-2 flex-shrink-0")
        SEllipsisTooltip.text-body-2( :content="item.name" )
</template>
